import { ErrorCard } from '@starsoft/common/components';
import { DepositMethodId } from '@starsoft/common/enums';
import { useSearchParams } from '@starsoft/common/hooks';
import { Nullable } from '@starsoft/common/interfaces';
import { DepositStatusId } from '@starsoft/common/models/deposit/status/id';
import { parseToBigInt } from '@starsoft/common/utils/parsers';
import { useDepositById } from '../../../../../../../api';
import ExternalRedirect from './ExternalRedirect';
import QrCodeResponse from './QrCodeResponse';
import styles from './styles.module.scss';

/**
 * DepositResponseContent Component
 * This component is responsible for rendering the appropriate response content
 * based on the deposit method and status retrieved from the URL search parameters.
 *
 * It utilizes the `useDepositById` hook to fetch deposit details and conditionally
 * renders either a QR code response or an external redirect based on the deposit method.
 *
 * @returns {JSX.Element} The rendered component for the deposit response.
 *
 * @example
 * // Usage example:
 * <DepositResponseContent />
 */
export default function DepositResponseContent() {
  // Retrieve search parameters from the URL
  const search: URLSearchParams = useSearchParams();
  // Get the 'method-id' from the search parameters
  const searchMethodId: Nullable<string> = search.get('method-id');
  // Determine the method ID, defaulting to null if not found
  const methodId: Nullable<string> = searchMethodId ? searchMethodId : null;
  // Get the 'deposit-id' from the search parameters
  const id: Nullable<string> = search.get('deposit-id');
  // Fetch deposit details using the deposit ID
  const { error, refetch, deposit, isLoading } = useDepositById({
    id: id ? (parseToBigInt(id) as bigint) : undefined,
  });

  // Render a loading skeleton if the data is still being fetched
  if (isLoading) {
    return <div className={styles.skeleton} />;
  }

  // Render an error card if there is an error fetching the deposit details
  if (error) {
    return <ErrorCard refetch={refetch} error={error} />;
  }

  // Determine the response content based on the method ID and deposit status
  switch (methodId?.toString()) {
    case DepositMethodId.SevenPay?.toString():
    case DepositMethodId.BetterbroBrazillianPix?.toString():
    case DepositMethodId.PixtopayBrazillianPix?.toString():
      // Render a QR code response if the deposit status is 'Created'
      if (
        deposit?.status?.id?.toString() ===
          DepositStatusId.Created?.toString() ||
        deposit?.statusId?.toString() === DepositStatusId.Created?.toString()
      ) {
        return <QrCodeResponse deposit={deposit} />;
      }
      // Otherwise, render an external redirect
      return <ExternalRedirect deposit={deposit} />;
    case DepositMethodId.BetterbroMercadoPago?.toString():
    case DepositMethodId.BetterbroMexicanSpei?.toString():
    case DepositMethodId.BetterbroUpi?.toString():
    case DepositMethodId.PayGamesIndiaUpi?.toString():
    case DepositMethodId.PayGamesIndonesiaQris?.toString():
    case DepositMethodId.PayGamesNigeria?.toString():
    case DepositMethodId.PayGamesSingaporePayNow?.toString():
    case DepositMethodId.PayGamesSouthAfrica?.toString():
      // Render an external redirect for these specific method IDs
      return <ExternalRedirect deposit={deposit} />;
    default:
      // Default to rendering an external redirect if no specific case matches
      return <ExternalRedirect deposit={deposit} />;
  }
}
